import { ChartDataset } from "chart.js";
import { lastDayOfMonth, lastDayOfWeek, min } from "date-fns";

import { ANALYSIS_DATA_TYPE, Sentiment, TimePeriod } from "@/constants";
import { SearchFilter } from "@/types/SearchFilter";
import {
  CHART_COLORS,
  CHART_SENTIMENT_COLORS,
  newDateWithCurrentTimezone,
} from "@/utils";
import { ReactLegendPluginOnclick } from "@components/Charts/plugins/ReactLegendPlugin";

type WidgetResult<T> = {
  results?: {
    labels: string[];
    dataset: Array<T>;
  };
  meta: { time_period: TimePeriod };
};

export type PackedWidgetData<T = unknown> = Array<WidgetResult<T>>;

export const unpackWidgetData = <T>(data?: PackedWidgetData<T>) => {
  const { dataset, labels } = data?.[0]?.results || { labels: [], dataset: [] };

  return { labels, datasets: dataset, meta: data?.[0]?.meta };
};

// Loops over the normalized data to create pie chart compatible props
export const toPieData = (
  datasets: ChartDataset<"line", number[]>[]
): {
  labels: string[];
  datasets: ChartDataset<"doughnut", number[]>[];
} => {
  const labels: string[] = [];
  const aggregatedData: number[] = [];
  const allAppliedFilters: Array<SearchFilter> = [];

  datasets.forEach(({ label, applied_filters, data }) => {
    const total = data.reduce((a, b) => a + b, 0);
    aggregatedData.push(total === 0 ? 1 : total);
    if (label) {
      labels.push(`${label} (${total == 0 ? "<1" : total}%)`);
      allAppliedFilters.push(applied_filters || {});
    }
  });

  let colors = CHART_COLORS;
  // Make sure the chart colors for sentiments are set
  if (
    datasets.length > 0 &&
    datasets.every(
      ({ applied_filters = { sentiment: undefined } }) =>
        applied_filters?.sentiment
    )
  ) {
    const order = datasets.map(
      (dataset) => dataset.applied_filters?.sentiment
    ) as Sentiment[];

    colors = order.map((sentiment) => CHART_SENTIMENT_COLORS[sentiment]);
  }

  return {
    labels,
    datasets: [
      {
        label: "",
        data: aggregatedData,
        backgroundColor: colors,
        applied_filters: allAppliedFilters,
      },
    ],
  };
};

export const reactLegendOnClickForSoundWithIndex: ReactLegendPluginOnclick = (
  chart,
  { datasetIndex, hidden }
) => {
  if (datasetIndex === undefined || hidden === undefined) return;

  const dataset = chart.data.datasets?.[datasetIndex];
  const currentlyOpen = chart.getVisibleDatasetCount();
  const type = dataset?.data_type;
  const aggregatedDatasetIndex = chart.data.datasets.findIndex(
    (d) => d.data_type === ANALYSIS_DATA_TYPE.ALL
  );

  // If the "ALL" dataset is toggled, handle hiding or showing all other datasets
  if (type === ANALYSIS_DATA_TYPE.ALL) {
    chart.setDatasetVisibility(aggregatedDatasetIndex, !!hidden);

    // If hidden, reverse visibility of all other datasets
    // ... or if the "all" is the only open one, toggle all the other ones.
    if (hidden || currentlyOpen === 1) {
      chart.data.datasets.forEach((_, i) => {
        if (i !== datasetIndex) chart.setDatasetVisibility(i, !hidden);
      });
    }

    chart.update();
    return;
  }

  // Set visibility of the selected dataset
  chart.setDatasetVisibility(datasetIndex, hidden);

  // If it's a "SPOKEN" dataset, toggle the corresponding "INDUSTRY_INDEX" dataset
  if (type === ANALYSIS_DATA_TYPE.SPOKEN) {
    const indexDatasetIndex = chart.data.datasets.findIndex(
      (d) => d.data_type === ANALYSIS_DATA_TYPE.INDUSTRY_INDEX
    );

    if (indexDatasetIndex > -1) {
      chart.setDatasetVisibility(indexDatasetIndex, hidden);
    }
  }

  // If hiding, and there's an "ALL" dataset, hide the aggregated dataset too
  if (hidden && aggregatedDatasetIndex > -1) {
    chart.setDatasetVisibility(aggregatedDatasetIndex, false);
  }

  chart.update();
};

export const tooltipWithDateRangeCallback = (
  labels: string[],
  timePeriod: undefined | TimePeriod,
  formatDate: (date: Date, format: string) => string,
  endDate?: string
) => {
  return (context: Array<{ dataIndex: number }>) => {
    if (!context || context.length === 0) {
      return;
    }

    const { dataIndex } = context[0];
    const label = labels[dataIndex];
    const startDate = newDateWithCurrentTimezone(label);

    if (!timePeriod || timePeriod === "day") {
      return formatDate(startDate, "PP");
    }

    const lastDay =
      timePeriod === "month"
        ? min(
            [
              lastDayOfMonth(startDate),
              endDate ? newDateWithCurrentTimezone(endDate) : new Date(),
            ].filter(Boolean)
          )
        : min(
            [
              lastDayOfWeek(startDate, { weekStartsOn: 1 }),
              endDate ? newDateWithCurrentTimezone(endDate) : new Date(),
            ].filter(Boolean)
          );

    return `${formatDate(startDate, "PP")} – ${formatDate(lastDay, "PP")}`;
  };
};
